import { datadogRum } from "@datadog/browser-rum"
import { makeTimestamp } from "@flockcover/flock-svc-core-types"
import { totalPayable } from "@flockcover/platform-types-common"
import { formatFloatTwoDecimals } from "@shared/utils/formatters/formatNumber"
import { Policy as PlatformPolicy, PolicyStatus } from "@shared/platform-api/generated"
import { createRequest, platform, createAxiosRetryConfig } from "@shared/platform-api/requests"
import { Collection, mapCollection } from "@shared/types"
import { timestampToString } from "@shared/utils/formatters/dates"
import { formatPremium } from "@shared/utils/formatters/formatPremium"
import { UUID } from "io-ts-types/lib/UUID"
import { createRequestHook } from "../../../../api/createRequestHook"
import { Policy, Status } from "./types"

export type Output = Collection<Policy>
type PlatformStatus = PolicyStatus["tag"]

export type Input = Readonly<{
  brokerId?: string
  claimsAdminId?: string
  carrierId?: string
  status?: Status
  search?: string
  limit?: number
}>

export const searchPolicies = createRequest<Input, Output>(async (input, getToken) => {
  const { status: rawStatus } = input
  const isActive = rawStatus === `Active` ? true : undefined
  const status = rawStatus !== undefined && !isActive ? toPlatformStatus(rawStatus) : undefined

  const retryConfig = createAxiosRetryConfig({
    onRetry: (retryCount, error) => {
      // Temporary tracking of retries
      datadogRum.addAction(`api-retry`, {
        location: `useSearchPolicies`,
        retryCount,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
      })
    },
  })

  const collection = await platform(getToken, retryConfig).policies.searchPolicies({
    brokerId: input.brokerId,
    claimsAdminId: input.claimsAdminId,
    carrierId: input.carrierId,
    search: input.search,
    limit: input.limit,
    status,
    isActive,
  })

  return mapCollection(collection, toPolicy)
})

export const useSearchPolicies = createRequestHook(searchPolicies)

const toPolicy = (item: PlatformPolicy): Policy => ({
  premium: formatPremium(totalPayable(item.writtenPremium), item.currency),
  product: `Connected Fleet Insurance`,
  issueDate: timestampToString(makeTimestamp(item.createdAt), `dd/MM/yyyy`),
  insuredName: item.insured.name.slice(0, 50),
  status: item.isActive ? `Active` : fromPlatformStatus(item.status.tag),
  nonQuoted: item.meta.nonQuoted ?? false,
  insuredId: item.insured.id as UUID,
  id: item.id as UUID,
  number: item.number,
  meta: item?.meta ?? undefined,
  connectivityRate: makeConnectivityRate(item.connectivityRatio ? item.connectivityRatio : undefined),
})

const toPlatformStatus = (key: Status): PlatformStatus => {
  switch (key) {
    case `Cancelled`:
      return `cancelled`
    case `Lapsed`:
      return `lapsed`
    case `Void`:
      return `void`
    default: {
      throw new Error(`Unknown Status: ${key}`)
    }
  }
}

const fromPlatformStatus = (key: PlatformStatus): Status => {
  switch (key) {
    case `cancelled`:
      return `Cancelled`
    case `lapsed`:
      return `Lapsed`
    case `bound`:
      return `Inactive`
    case `void`:
      return `Void`
    case `renewed`:
      return `Inactive`
    default: {
      throw new Error(`Unknown Status: ${key}`)
    }
  }
}

const makeConnectivityRate = (connectivityRate: number | undefined): number | null =>
  connectivityRate !== undefined ? formatFloatTwoDecimals(connectivityRate) : null
